/**
 * One ticket as a list row: number, title, the start of its description, status, priority and when
 * it was last touched. The Tickets page and the customer page both list tickets, and two copies of
 * this card is how the two lists would come to disagree about what a ticket row says.
 */
import { insets } from 'webwin';
import type { TicketDto } from '../../contracts.gen.js';
import { join, localDate, trim } from '../../format.js';
import type { Card } from '../../ui/Card.js';
import { chip } from '../../ui/Chip.js';
import { card, fillWith, FONT, GAP, hstack, inlineRow, muted, splitRow, stackOf, value, vstack } from '../../ui/layout.js';
import { PRIORITY_NAMES } from './recurring.js';

export interface TicketCardOptions {
  /** Shown on a list that spans customers; omitted where the page is already about one. */
  customerName?: string;
  onOpen: (ticket: TicketDto) => void;
}

export function ticketCard(t: TicketDto, options: TicketCardOptions): Card {
  const heading = inlineRow();
  heading.controls.add(value(`#${t.id}`, { font: FONT.small, role: 'muted' }));
  heading.controls.add(value(t.title, { font: FONT.bold }));
  if (t.isRecurring) heading.controls.add(chip('recurring', 'info'));

  const left = stackOf(vstack({ spacing: GAP.xs }), heading);
  if (options.customerName) left.controls.add(muted(options.customerName));
  if (t.description) left.controls.add(muted(trim(t.description, 120)));

  const right = stackOf(
    vstack({ spacing: 0, autoWidth: true, align: 'end' }),
    value(t.statusLabel, { font: FONT.small }),
    value(join([PRIORITY_NAMES[t.priority], localDate(t.updatedUtc)]), {
      font: FONT.small,
      role: 'muted'
    })
  );

  const row = splitRow({ align: 'center' });
  fillWith(row, left);
  row.controls.add(right);

  const shell = card({ padding: insets(12), cursor: 'pointer' });
  shell.controls.add(row);
  shell.onActivate(() => options.onOpen(t));
  return shell;
}
